/* CaseHistoryTab — Chronological revision history for a case.
 *
 * Shows every iteration of the round-trip between doctor and planner:
 *   - Plan v1 sent for approval
 *   - Doctor's revision request 1 (with items + structured_changes)
 *   - Planner's response to revision 1 (decisions[])
 *   - Plan v2 sent for approval
 *   - Doctor's revision request 2
 *   - …
 *
 * Each row is collapsible; the doctor can expand any entry to see the
 * full per-item detail. Lives as a permanent tab next to Treatment
 * Plan so iter 2+ is always one click away from the full audit trail.
 *
 * iter98.99bl
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    ClockClockwise, CaretDown, CaretUp, ArrowsClockwise, NotePencil,
    Sparkle, CheckCircle, XCircle, FileText, ArrowRight, Clock,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { expandRevisionItems } from "@/lib/revisionSections";


const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
            hour: "numeric", minute: "2-digit",
        });
    } catch { return "—"; }
};

const RETAINER_LABEL = {
    vivera: "Clear", hawley: "Hawley", fixed_bonded: "Fixed bonded", dual: "Dual", other: "Other",
};
const fmtVal = (v, unit, planKey) => {
    if (v === undefined || v === null || v === "") return "—";
    if (planKey === "retainer_type") return RETAINER_LABEL[v] || v;
    return unit ? `${v} ${unit}`.trim() : String(v);
};


export default function CaseHistoryTab({ c }) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!c?.id) return;
        let alive = true;
        api.get(`/cases/${c.id}/plan-versions`)
            .then((r) => { if (alive) setVersions(r.data || []); })
            .catch(() => { if (alive) setVersions([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [c?.id]);

    /* Build a unified chronological feed of events:
       - plan snapshots (with `reason`: sent_for_approval | revision_requested | approved)
       - doctor revision requests (from c.revision_requests[])
       - planner responses (decisions[]) attached to each revision request
       The feed is grouped into ITERATION ROUNDS: each round is one
       plan-version-sent-for-approval ± its revision/response (if any). */
    const rounds = useMemo(() => buildRounds(c, versions), [c, versions]);

    if (loading) {
        return <div className="p-8 text-center text-sm text-muted-foreground">Loading history…</div>;
    }
    if (rounds.length === 0) {
        return (
            <div className="rounded-2xl border border-border bg-card p-8 text-center" data-testid="case-history-empty">
                <ClockClockwise size={28} weight="duotone" className="mx-auto text-muted-foreground mb-2" />
                <div className="text-sm text-muted-foreground">
                    No plan history yet — the planner&apos;s first version will appear here once submitted.
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="case-history-tab">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground px-1">
                Plan history · {rounds.length} round{rounds.length === 1 ? "" : "s"}
            </div>
            {rounds.map((round, i) => (
                <RoundCard key={round.version} round={round} isLatest={i === 0} />
            ))}
        </div>
    );
}


function RoundCard({ round, isLatest }) {
    const [open, setOpen] = useState(isLatest);
    const rows = round.rev ? expandRevisionItems(round.rev, round.beforePlan) : [];
    const decisionByKey = useMemo(() => {
        const m = {};
        (round.rev?.decisions || []).forEach((d) => { m[d.key] = d; });
        return m;
    }, [round.rev]);
    const accepted = rows.filter((r) => {
        const d = decisionByKey[r.key];
        return d?.decision === "accepted" || (round.rev?.actioned_items || []).includes(r.key);
    }).length;
    const denied = rows.filter((r) => decisionByKey[r.key]?.decision === "denied").length;

    return (
        <div
            className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm"
            data-testid={`case-history-round-${round.version}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full bg-secondary/60 hover:bg-secondary px-4 py-3 border-b border-border/70 flex items-center gap-3 transition-colors text-left"
                aria-expanded={open}
            >
                <span className={`shrink-0 w-[3px] h-5 rounded-sm ${round.outcome === "approved" ? "bg-emerald-500" : round.outcome === "revision_requested" ? "bg-rose-500" : "bg-primary"}`} />
                <div className="flex-1 min-w-0">
                    <div className="font-display text-sm font-semibold text-foreground flex items-center gap-2">
                        <span>Plan v{round.version}</span>
                        {isLatest && <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">Latest</span>}
                        <OutcomePill outcome={round.outcome} />
                    </div>
                    <div className="text-[11px] text-muted-foreground/80 truncate mt-0.5">
                        Sent {fmtDateTime(round.sentAt)}
                        {round.rev && (
                            <> · doctor requested {rows.length} change{rows.length === 1 ? "" : "s"} · {accepted} addressed{denied ? ` · ${denied} declined` : ""}</>
                        )}
                    </div>
                </div>
                {open ? <CaretUp size={14} weight="bold" className="text-muted-foreground shrink-0" />
                      : <CaretDown size={14} weight="bold" className="text-muted-foreground shrink-0" />}
            </button>

            {open && (
                <div className="p-4 sm:p-5 space-y-4">
                    {/* Planner submission event */}
                    <Event
                        icon={Sparkle}
                        title="Planner submitted this version"
                        time={round.sentAt}
                        who={round.plannerName}
                    >
                        {round.planNote && (
                            <div className="mt-1.5 text-[12px] text-foreground/80 italic">“{round.planNote}”</div>
                        )}
                    </Event>

                    {/* Doctor's revision request (if any) */}
                    {round.rev && (
                        <Event
                            icon={NotePencil}
                            title={`Doctor requested ${rows.length} change${rows.length === 1 ? "" : "s"}`}
                            time={round.rev.requested_at}
                            who={round.rev.requested_by_name || "Doctor"}
                            tone="amber"
                        >
                            {round.rev.note && (
                                <div className="mt-1.5 text-[12px] text-foreground/80 italic">“{round.rev.note}”</div>
                            )}
                            {rows.length > 0 && (
                                <ul className="mt-2 space-y-1 list-disc list-inside text-[12.5px] text-foreground/80">
                                    {rows.map((row) => (
                                        <li key={row.key} data-testid={`history-row-${round.version}-${row.key}`}>
                                            <strong>{row.label}:</strong>{" "}
                                            {row.kind === "struct"
                                                ? <span className="tabular-nums">{fmtVal(row.from, row.unit, row.planKey)} → {fmtVal(row.to, row.unit, row.planKey)}</span>
                                                : <span className="italic">“{row.note}”</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Event>
                    )}

                    {/* Planner's per-row response (if actioned) */}
                    {round.rev?.status === "actioned" && (
                        <Event
                            icon={ArrowsClockwise}
                            title="Planner responded"
                            time={round.rev.actioned_at}
                            who={round.rev.actioned_by_name || "Planner"}
                            tone="teal"
                        >
                            <div className="mt-2 rounded-lg border border-border divide-y divide-border bg-card">
                                {rows.map((row) => {
                                    const d = decisionByKey[row.key];
                                    const k = d?.decision === "accepted" ? "accepted"
                                            : d?.decision === "denied"   ? "denied"
                                            : ((round.rev.actioned_items || []).includes(row.key) ? "accepted" : "unknown");
                                    return (
                                        <div key={row.key} className="px-3 py-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[12.5px]">
                                            <div className="font-semibold">{row.label}</div>
                                            <div className="text-foreground/80">
                                                {k === "accepted" && d?.applied_value !== undefined && d?.applied_value !== null
                                                    ? <>Applied: <strong>{fmtVal(d.applied_value, row.unit, row.planKey)}</strong></>
                                                    : k === "denied"
                                                        ? (d?.rationale ? <>Declined: <span className="italic">{d.rationale}</span></> : "Declined")
                                                        : k === "accepted"
                                                            ? (d?.rationale ? <>Applied: <span className="italic">{d.rationale}</span></> : "Applied to plan")
                                                            : <span className="italic text-muted-foreground">No decision</span>}
                                            </div>
                                            <div className="md:text-right">
                                                <DecisionPill kind={k} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Event>
                    )}

                    {/* Plan version JSON link (download/inspect) */}
                    {round.snapshotId && (
                        <div className="text-[11px] text-muted-foreground/80">
                            <FileText size={11} weight="duotone" className="inline mr-1 align-middle" />
                            Plan snapshot ID: <span className="font-mono">{round.snapshotId.slice(0, 8)}…</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function Event({ icon: Icon, title, time, who, tone = "primary", children }) {
    const toneRingCls = {
        primary: "ring-primary/30 bg-primary/10 text-primary",
        amber:   "ring-amber-300 bg-amber-100 text-amber-700",
        teal:    "ring-emerald-300 bg-emerald-100 text-emerald-700",
    }[tone];
    return (
        <div className="flex gap-3">
            <div className={`shrink-0 w-8 h-8 rounded-full grid place-items-center ring-1 ${toneRingCls}`}>
                <Icon size={14} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[13px] font-semibold text-foreground">{title}</div>
                    <div className="text-[11px] text-muted-foreground/80 tabular-nums">
                        <Clock size={9} weight="duotone" className="inline mr-1 align-middle" />
                        {fmtDateTime(time)}{who ? ` · ${who}` : ""}
                    </div>
                </div>
                {children}
            </div>
        </div>
    );
}


function OutcomePill({ outcome }) {
    const map = {
        approved:           { Icon: CheckCircle, label: "Approved",           cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
        revision_requested: { Icon: NotePencil,  label: "Revision requested", cls: "bg-rose-100 text-rose-800 border-rose-200" },
        pending:            { Icon: Clock,       label: "Pending",            cls: "bg-amber-100 text-amber-800 border-amber-200" },
        sent:               { Icon: Sparkle,     label: "Sent",               cls: "bg-primary/10 text-primary border-primary/30" },
    };
    const e = map[outcome] || { Icon: Clock, label: outcome || "Unknown", cls: "bg-secondary text-muted-foreground border-border" };
    const Icon = e.Icon;
    return (
        <span className={`inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.14em] border rounded-full px-1.5 py-0.5 ${e.cls}`}>
            <Icon size={9} weight="fill" />
            {e.label}
        </span>
    );
}

function DecisionPill({ kind }) {
    const map = {
        accepted: { Icon: CheckCircle, label: "Addressed", cls: "bg-primary/10 text-primary border-primary/30" },
        denied:   { Icon: XCircle,     label: "Declined",  cls: "bg-rose-100 text-rose-800 border-rose-200" },
        unknown:  { Icon: Clock,       label: "Pending",   cls: "bg-amber-100 text-amber-800 border-amber-200" },
    };
    const e = map[kind] || map.unknown;
    const Icon = e.Icon;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] border rounded-full px-2 py-0.5 ${e.cls}`}>
            <Icon size={11} weight="fill" />
            {e.label}
        </span>
    );
}


/* Build the round feed from a case + plan_versions list. Each round is
   a doctor-facing "plan vN sent for review" — that can be either the
   initial `sent_for_approval` snapshot OR a `planner_resubmit` snapshot
   (which happens when the planner closes a revision round by sending
   the updated plan back). Each round carries (a) the doctor's matching
   revision_request (if any) and (b) the outcome
   (approved | revision_requested | pending). */
function buildRounds(c, versions) {
    if (!c) return [];
    const sortedVersions = [...versions].sort((a, b) => (a.version || 0) - (b.version || 0));
    // Both reasons start a new doctor-review round.
    const sentVersions = sortedVersions.filter(
        (v) => v.reason === "sent_for_approval" || v.reason === "planner_resubmit"
    );
    const revisions = (c.revision_requests || [])
        .slice()
        .sort((a, b) => new Date(a.requested_at) - new Date(b.requested_at));
    const approvedSnap = versions.find((vv) => vv.reason === "approved");

    return sentVersions.map((v, idx) => {
        // The revision that BELONGS to this round is the one requested
        // after this version was sent but BEFORE the next sent version
        // (if any).
        const nextSent = sentVersions[idx + 1];
        const matchingRev = revisions.find((r) => {
            if (!r.requested_at) return false;
            const t = new Date(r.requested_at).getTime();
            if (t < new Date(v.created_at).getTime()) return false;
            if (nextSent && t >= new Date(nextSent.created_at).getTime()) return false;
            return true;
        });
        // Outcome: approved if there's an approved snapshot after THIS
        // version and no further sent version. Revision-requested when
        // a matching revision exists. Else pending.
        const outcome = (approvedSnap && !nextSent &&
                         new Date(approvedSnap.created_at) >= new Date(v.created_at))
            ? "approved"
            : matchingRev
                ? "revision_requested"
                : "pending";
        return {
            // Display version = 1, 2, 3 … (the order of doctor-review
            // rounds, NOT the raw snapshot version which also counts
            // intermediate `revision_requested` snapshots).
            version: idx + 1,
            sentAt: v.created_at,
            plannerName: v.snapshotted_by_name || "Planner",
            planNote: v.plan?.planner_notes || "",
            snapshotId: v.id,
            beforePlan: v.plan,
            rev: matchingRev,
            outcome,
        };
    }).reverse(); // newest first
}
